import { safeParseJson } from '@/utils/safeParseJson';
import type { AnchorCategory, DeviceAvailability, PublishDevice } from './types';

// Shown when the Web (appless) platform is gated behind its staged rollout.
export const APPLESS_GATE_REASON = 'Coming soon';

// Shown when a platform isn't one the scene was published/submitted to.
export const NOT_PUBLISHED_REASON = "Not published to this platform";

const PUBLISH_DEVICES: readonly PublishDevice[] = ['app', 'appless', 'spectacles'];

// The platforms a scene was published/submitted to, read from
// scene_instance_payload.devices (an array set by the publish flow). Returns
// null when there is no array to gate on — drafts and legacy build-device
// payloads (where `devices` is a single string) stay ungated.
export function getPublishedDevices(
  payload: Record<string, unknown> | string | null | undefined,
): PublishDevice[] | null {
  const parsed = safeParseJson<Record<string, unknown>>(payload);
  const devices = parsed?.devices;
  if (!Array.isArray(devices)) return null;
  return devices.filter((d): d is PublishDevice => PUBLISH_DEVICES.includes(d as PublishDevice));
}

export function getDeviceAvailability(
  anchorCategory: AnchorCategory | undefined,
  opts?: { canUseAppless?: boolean },
): Record<PublishDevice, DeviceAvailability> {
  const spectaclesDisabledForImmersal = anchorCategory === 'immersal';
  const applessGated = opts?.canUseAppless === false;
  return {
    app: { state: 'selectable' },
    appless: applessGated
      ? { state: 'disabled', disabledReason: APPLESS_GATE_REASON }
      : { state: 'selectable' },
    spectacles: spectaclesDisabledForImmersal
      ? { state: 'disabled', disabledReason: "Immersal scenes aren't supported on Spectacles yet" }
      : { state: 'selectable' },
  };
}

export const DEVICE_LABELS: Record<PublishDevice, string> = {
  app: 'App',
  appless: 'Web',
  spectacles: 'Specs',
};

export const DEVICE_DESCRIPTIONS: Record<PublishDevice, string> = {
  app: 'Browse and play inside the Lureo mobile app',
  appless: 'Open instantly via App Clip on iOS, no install',
  spectacles: 'Play through the Lureo lens on Snap Spectacles',
};
